import { isDsd, getDsdLabel } from './formatDetection';

const SUFFIX_PATTERN = /\s*[\(\[](remaster(ed)?|deluxe(\s+edition)?|mono|stereo|sacd|dsd|hi-?res|expanded|anniversary(\s+edition)?|super\s+deluxe|bonus\s+track(s)?|special\s+edition|collector'?s?\s+edition|limited\s+edition|original\s+(master|recording)|reissue|\d{4}\s+re-?master)[\)\]]\s*/gi;
const BRACKET_SUFFIX = /\s*\[[^\]]*\]\s*$/;
const LEADING_THE = /^the\s+/i;

export function normalizeGroupKey(title, artist) {
  let t = (title || '').toLowerCase().trim();
  let a = (artist || '').toLowerCase().trim();

  t = t.replace(SUFFIX_PATTERN, '').replace(BRACKET_SUFFIX, '').trim();
  a = a.replace(LEADING_THE, '').trim();
  t = t.replace(/\s+/g, ' ');
  a = a.replace(/\s+/g, ' ');

  return `${a} — ${t}`;
}

export function getVersionQuality(album) {
  if (isDsd(album)) return getDsdLabel(album) || 'DSD';
  const bd = album.bit_depth;
  const sr = album.sample_rate;
  if (bd && sr) {
    const khz = Math.round(Number(sr) / 1000);
    return `${bd}/${khz}`;
  }
  if (sr) return `${Math.round(Number(sr) / 1000)} kHz`;
  if (bd) return `${bd}-bit`;
  return '';
}

export function getVersionSource(album) {
  const meta = album.metadata || {};
  const src = album.source_label || album.source_name || '';
  if (src) return src;
  if (album.__source === 'local_library' || album.isLocalLibrary) return 'Local';
  return 'Unknown';
}

function getQualityScore(album) {
  if (isDsd(album)) {
    const sr = Number(album.sample_rate);
    if (sr >= 22579200) return 900;
    if (sr >= 11289600) return 800;
    if (sr >= 5644800) return 700;
    return 600;
  }
  const bd = Number(album.bit_depth) || 16;
  const sr = Number(album.sample_rate) || 44100;
  return bd * 10 + Math.round(sr / 1000);
}

export function groupVersions(albums, overrides = {}) {
  const groups = new Map();

  for (const album of albums) {
    const id = album.album_key || album.id;
    if (!id) continue;

    if (overrides.excluded?.[id]) continue;

    let groupId = null;
    let confidence = 'title_match';

    if (overrides.manual?.[id]) {
      groupId = overrides.manual[id];
      confidence = 'user_confirmed';
    } else {
      const mbRg = album.mb_release_group_id || album.metadata?.mb_release_group_id;
      if (mbRg) {
        groupId = `mb:${mbRg}`;
        confidence = 'musicbrainz';
      } else {
        groupId = normalizeGroupKey(album.title, album.album_artist || album.artist_name);
      }
    }

    if (!groups.has(groupId)) {
      groups.set(groupId, {
        groupId,
        groupTitle: album.title,
        groupArtist: album.album_artist || album.artist_name,
        confidence,
        versions: [],
      });
    }

    const group = groups.get(groupId);
    if (confidence === 'musicbrainz' && group.confidence !== 'user_confirmed') {
      group.confidence = 'musicbrainz';
    }

    group.versions.push({
      albumId: id,
      title: album.title,
      format: (album.format || '').toUpperCase(),
      quality: getVersionQuality(album),
      qualityScore: getQualityScore(album),
      source: getVersionSource(album),
      label: album.metadata?.label || album.label || '',
      year: album.year || '',
      trackCount: album.track_count || 0,
      totalDuration: album.total_duration || 0,
      totalSize: album.total_size || 0,
      dynamicRange: album.metadata?.dynamic_range || album.dynamic_range || null,
      isHires: album.is_hires,
      isLossless: album.is_lossless,
      coverUrl: album.cover_art_url,
      isPrimary: false,
      album,
    });
  }

  const result = [];
  for (const [, group] of groups) {
    if (group.versions.length < 2) continue;

    group.versions.sort((a, b) => b.qualityScore - a.qualityScore);

    const primaryId = overrides.primary?.[group.groupId];
    let primarySet = false;
    if (primaryId) {
      for (const v of group.versions) {
        if (v.albumId === primaryId) { v.isPrimary = true; primarySet = true; break; }
      }
    }
    if (!primarySet && group.versions.length > 0) {
      group.versions[0].isPrimary = true;
    }

    group.groupTitle = group.versions.find(v => v.isPrimary)?.title || group.versions[0].title;
    group.groupArtist = group.versions.find(v => v.isPrimary)?.album?.artist_name || group.groupArtist;

    result.push(group);
  }

  return result;
}

export function buildVersionMap(groups) {
  const map = new Map();
  for (const group of groups) {
    for (const v of group.versions) {
      map.set(v.albumId, { group, version: v });
    }
  }
  return map;
}

export function getVersionCount(albumId, versionMap) {
  const entry = versionMap.get(albumId);
  return entry ? entry.group.versions.length : 0;
}

/** Backend grouped-pressing count from connector v11.0.115+ (preferred over client groupVersions). */
export function getBackendVersionCount(album) {
  const n = Number(album?.version_count);
  if (Number.isFinite(n) && n > 0) return n;
  return 0;
}

export function folderLabelFromPath(path) {
  if (path == null || path === '') return 'Unknown folder';
  const normalized = String(path).replace(/\\/g, '/').replace(/\/+$/, '');
  const parts = normalized.split('/').filter(Boolean);
  return parts[parts.length - 1] || normalized || 'Unknown folder';
}

export function formatVersionQualityFromFields(version) {
  if (isDsd(version)) return getDsdLabel(version) || 'DSD';
  const bd = version?.bit_depth;
  const sr = version?.sample_rate;
  if (bd && sr) {
    const khz = Math.round(Number(sr) / 1000);
    return `${bd}/${khz}`;
  }
  if (sr) return `${Math.round(Number(sr) / 1000)} kHz`;
  if (bd) return `${bd}-bit`;
  return '';
}

export function formatVersionQuality(album) {
  return formatVersionQualityFromFields(album);
}

export function getVersionGroup(albumId, versionMap) {
  return versionMap.get(albumId)?.group || null;
}
